import React, { useEffect, useRef, CSSProperties } from 'react';
import { Image } from '@/components/Image';

interface NotificationBadgeProps {
  message?: string;
  position?: { top?: number; right?: number; bottom?: number; left?: number };
  onClose?: () => void;
  /** 자동 닫힘 시간(ms). 지정하지 않으면 닫히지 않습니다 */
  autoCloseMs?: number;
}

export const NotificationBadge: React.FC<NotificationBadgeProps> = ({
  message = 'We’ve notified a staff member. Please wait a moment.',
  position = { top: 0, left: 0 },
  onClose,
  autoCloseMs = 5000 // 기본 5초
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef<NotificationBadgeProps['onClose']>(onClose);

  // 최신 onClose를 ref에 유지
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // 바깥 클릭 닫기
  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onCloseRef.current?.();
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // 자동 닫기: 타이머는 마운트 시 한 번만 설정
  useEffect(() => {
    if (!autoCloseMs) return;
    const timer = window.setTimeout(() => {
      onCloseRef.current?.();
    }, autoCloseMs);
    return () => window.clearTimeout(timer);
  }, [autoCloseMs]); // 시간 변경 시에만 재설정

  const style: CSSProperties = { position: 'absolute', ...position };

  return (
    <div
      className="px-3 py-[6px] bg-black/85 text-white text-[13px] font-medium rounded h-8 flex items-center pointer-events-auto select-none whitespace-nowrap z-[100] w-[190%] text-center justify-center"
      ref={ref}
      style={style}
    >
      <Image
        src={'/icons/triangle.svg'}
        alt="photocard-triangle"
        width={10}
        height={6}
        style={{ position: 'absolute', left: -9, top: 7 }}
      />
      {message}
    </div>
  );
};
